import { css } from 'styled-components';
import { commonBreakpointsValues } from '@lmn/design-system';

type InterpolatedFunction = ReturnType<typeof css>;
type ThemedProps = { theme: { kallisto: Record<string, unknown> } };

const spacePrimitives: Record<string, string> = {
  spaceNegative200: '-16px',
  spaceNegative100: '-8px',
  spaceNegative50: '-4px',
  space0: '0px',
  space50: '4px',
  space100: '8px',
  space150: '12px',
  space200: '16px',
  space250: '20px',
  space300: '24px',
  space400: '32px',
  space500: '40px',
  space600: '48px',
  space700: '56px',
  space800: '64px',
  space900: '72px',
  space1000: '80px',
  space1100: '88px',
  space1200: '96px',
  space1300: '104px',
  space1500: '120px',
};

const baseSpacingPxUnit = 8;

interface SpacingDescriptor {
  hor?: string | number;
  ver?: string | number;
  top?: string | number;
  bottom?: string | number;
  left?: string | number;
  right?: string | number;
}

type SpacingValue = string | number | SpacingDescriptor;

function spacing(value: string | number): string {
  if (value === 'auto') return 'auto';
  if (typeof value === 'number') return `${value * baseSpacingPxUnit}px`;
  return spacePrimitives[value] ?? '';
}

function paddingFn(descriptor: SpacingValue): string {
  if (typeof descriptor === 'number' || typeof descriptor === 'string') {
    return `padding: ${spacing(descriptor)};`;
  }
  if (!descriptor) return '';
  let styles = '';
  if (typeof descriptor.hor !== 'undefined') {
    const value = spacing(descriptor.hor);
    styles += `padding-left: ${value}; padding-right: ${value};`;
  }
  if (typeof descriptor.ver !== 'undefined') {
    const value = spacing(descriptor.ver);
    styles += `padding-top: ${value}; padding-bottom: ${value};`;
  }
  if (typeof descriptor.top !== 'undefined') styles += `padding-top: ${spacing(descriptor.top)};`;
  if (typeof descriptor.bottom !== 'undefined') styles += `padding-bottom: ${spacing(descriptor.bottom)};`;
  if (typeof descriptor.left !== 'undefined') styles += `padding-left: ${spacing(descriptor.left)};`;
  if (typeof descriptor.right !== 'undefined') styles += `padding-right: ${spacing(descriptor.right)};`;
  return styles;
}

function marginFn(descriptor: SpacingValue): string {
  if (typeof descriptor === 'number' || typeof descriptor === 'string') {
    return `margin: ${spacing(descriptor)};`;
  }
  if (!descriptor) return '';
  let styles = '';
  if (typeof descriptor.hor !== 'undefined') {
    const value = spacing(descriptor.hor);
    styles += `margin-left: ${value}; margin-right: ${value};`;
  }
  if (typeof descriptor.ver !== 'undefined') {
    const value = spacing(descriptor.ver);
    styles += `margin-top: ${value}; margin-bottom: ${value};`;
  }
  if (typeof descriptor.top !== 'undefined') styles += `margin-top: ${spacing(descriptor.top)};`;
  if (typeof descriptor.bottom !== 'undefined') styles += `margin-bottom: ${spacing(descriptor.bottom)};`;
  if (typeof descriptor.left !== 'undefined') styles += `margin-left: ${spacing(descriptor.left)};`;
  if (typeof descriptor.right !== 'undefined') styles += `margin-right: ${spacing(descriptor.right)};`;
  return styles;
}

type FontStyle = {
  fontSize: unknown;
  lineHeight: unknown;
  fontWeight: unknown;
  fontFamily: unknown;
};

function getResponsiveVariantStyles(kallisto: Record<string, unknown>, variant: string): InterpolatedFunction {
  const font = kallisto.font as Record<string, Record<string, unknown>>;
  const styles: Record<string, { mobile: FontStyle; desktop: FontStyle }> = {
    Display: {
      mobile: {
        fontSize: font.size[425],
        lineHeight: font.lineHeight[500],
        fontWeight: font.weight.bold,
        fontFamily: font.family.sans,
      },
      desktop: {
        fontSize: font.size[700],
        lineHeight: font.lineHeight[850],
        fontWeight: font.weight.bold,
        fontFamily: font.family.sans,
      },
    },
    Title1: {
      mobile: {
        fontSize: font.size[300],
        lineHeight: font.lineHeight[375],
        fontWeight: font.weight.bold,
        fontFamily: font.family.sans,
      },
      desktop: {
        fontSize: font.size[400],
        lineHeight: font.lineHeight[475],
        fontWeight: font.weight.bold,
        fontFamily: font.family.sans,
      },
    },
    Title2: {
      mobile: {
        fontSize: font.size[275],
        lineHeight: font.lineHeight[400],
        fontWeight: font.weight.bold,
        fontFamily: font.family.sans,
      },
      desktop: {
        fontSize: font.size[300],
        lineHeight: font.lineHeight[375],
        fontWeight: font.weight.bold,
        fontFamily: font.family.sans,
      },
    },
  };
  const { mobile, desktop } = styles[variant];
  return css`
    font-size: ${mobile.fontSize};
    line-height: ${mobile.lineHeight};
    font-weight: ${mobile.fontWeight};
    font-family: ${mobile.fontFamily};
    @media (min-width: ${commonBreakpointsValues['breakpoints-s']}px) {
      font-size: ${desktop.fontSize};
      line-height: ${desktop.lineHeight};
      font-weight: ${desktop.fontWeight};
      font-family: ${desktop.fontFamily};
    }
  `;
}

function getNonResponsiveVariantStyles(kallisto: Record<string, unknown>, variant: string): InterpolatedFunction {
  const font = kallisto.font as Record<string, Record<string, unknown>>;
  const styles: Record<string, FontStyle> = {
    Title3: {
      fontSize: font.size[250],
      lineHeight: font.lineHeight[375],
      fontWeight: font.weight.bold,
      fontFamily: font.family.sans,
    },
    Title4: {
      fontSize: font.size[225],
      lineHeight: font.lineHeight[350],
      fontWeight: font.weight.bold,
      fontFamily: font.family.sans,
    },
    Title5: {
      fontSize: font.size[200],
      lineHeight: font.lineHeight[300],
      fontWeight: font.weight.bold,
      fontFamily: font.family.sans,
    },
    Title6: {
      fontSize: font.size[175],
      lineHeight: font.lineHeight[250],
      fontWeight: font.weight.bold,
      fontFamily: font.family.sans,
    },
    Body: {
      fontSize: font.size[200],
      lineHeight: font.lineHeight[300],
      fontWeight: font.weight.regular,
      fontFamily: font.family.sans,
    },
    BodySmall: {
      fontSize: font.size[175],
      lineHeight: font.lineHeight[250],
      fontWeight: font.weight.regular,
      fontFamily: font.family.sans,
    },
    Caption: {
      fontSize: font.size[150],
      lineHeight: font.lineHeight[225],
      fontWeight: font.weight.regular,
      fontFamily: font.family.sans,
    },
  };
  const variantStyle = styles[variant];
  return css`
    font-size: ${variantStyle.fontSize};
    line-height: ${variantStyle.lineHeight};
    font-weight: ${variantStyle.fontWeight};
    font-family: ${variantStyle.fontFamily};
  `;
}

function getAccentVariantStyles(kallisto: Record<string, unknown>, variant: string): InterpolatedFunction {
  const font = kallisto.font as Record<string, Record<string, unknown>>;
  const baseVariant = variant.replace('Accent', '');
  const styles: Record<string, { mobile: FontStyle; desktop: FontStyle }> = {
    Display: {
      mobile: {
        fontSize: font.size[425],
        lineHeight: font.lineHeight[500],
        fontWeight: font.weight.regular,
        fontFamily: font.family.script,
      },
      desktop: {
        fontSize: font.size[700],
        lineHeight: font.lineHeight[850],
        fontWeight: font.weight.regular,
        fontFamily: font.family.script,
      },
    },
    Title1: {
      mobile: {
        fontSize: font.size[300],
        lineHeight: font.lineHeight[375],
        fontWeight: font.weight.regular,
        fontFamily: font.family.script,
      },
      desktop: {
        fontSize: font.size[400],
        lineHeight: font.lineHeight[475],
        fontWeight: font.weight.regular,
        fontFamily: font.family.script,
      },
    },
    Title2: {
      mobile: {
        fontSize: font.size[275],
        lineHeight: font.lineHeight[400],
        fontWeight: font.weight.regular,
        fontFamily: font.family.script,
      },
      desktop: {
        fontSize: font.size[300],
        lineHeight: font.lineHeight[375],
        fontWeight: font.weight.regular,
        fontFamily: font.family.script,
      },
    },
  };
  const { mobile, desktop } = styles[baseVariant];
  return css`
    font-size: ${mobile.fontSize};
    line-height: ${mobile.lineHeight};
    font-weight: ${mobile.fontWeight};
    font-family: ${mobile.fontFamily};
    @media (min-width: ${commonBreakpointsValues['breakpoints-s']}px) {
      font-size: ${desktop.fontSize};
      line-height: ${desktop.lineHeight};
      font-weight: ${desktop.fontWeight};
      font-family: ${desktop.fontFamily};
    }
  `;
}

function fontMixin(variant: string): InterpolatedFunction {
  return css`
    ${(props: ThemedProps) => {
      const kallisto = props.theme.kallisto;
      if (variant === 'Display' || variant === 'Title1' || variant === 'Title2') {
        return getResponsiveVariantStyles(kallisto, variant);
      }
      if (variant === 'DisplayAccent' || variant === 'Title1Accent' || variant === 'Title2Accent') {
        return getAccentVariantStyles(kallisto, variant);
      }
      return getNonResponsiveVariantStyles(kallisto, variant);
    }}
  `;
}

function borderRadiusMixin(radius: string): InterpolatedFunction {
  return css`
    border-radius: ${(props: ThemedProps) => {
      const k = props.theme.kallisto as Record<string, Record<string, unknown>>;
      return k.border.radius[radius] as string;
    }};
  `;
}

function shadowMixin(shadow: string): InterpolatedFunction {
  return css`
    box-shadow: ${(props: ThemedProps) => {
      const k = props.theme.kallisto as Record<string, Record<string, unknown>>;
      return k.shadow[shadow] as string;
    }};
  `;
}

function paddingMixin(descriptor: SpacingValue): InterpolatedFunction {
  return css`
    ${() => paddingFn(descriptor)}
  `;
}

function marginMixin(descriptor: SpacingValue): InterpolatedFunction {
  return css`
    ${() => marginFn(descriptor)}
  `;
}

export const kallistoMixins = {
  font: fontMixin,
  padding: paddingMixin,
  margin: marginMixin,
  spacing,
  borderRadius: borderRadiusMixin,
  shadow: shadowMixin,
};
